"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import toast from "react-hot-toast";
import ButtonBlueBorder from "@/app/components/appcomponents/ButtonBlueBorder";
import TextFieldComp from "@/app/components/appcomponents/TextFieldComp";
import { PopupShell } from "@/app/components/appcomponents/thankYouWizard/parts";
import orderService from "@/services/order-service";
import SelectField from "./SelectField";
import MemoryPageField from "./MemoryPageField";
import { CANDLE_OPTIONS, EXAMPLE_MEMORY_URL } from "./orderConfig";
import { useAuth } from "@/hooks/useAuth";

// "Tule" opens the example memory page.
function ExampleLink({ children }) {
  return (
    <a href={EXAMPLE_MEMORY_URL} target="_blank" rel="noopener noreferrer" className="text-[#0A85C2] underline">
      {children}
    </a>
  );
}

/**
 * Premium animated candle (1 or 5 years) for a memory page.
 * Whoever buys it also prolongs the Skrbnik status of that page by the same time.
 * Login is required to purchase.
 */
export default function CandleOrderModal({ obituary, preset = "", onClose }) {
  const { isAuthenticated } = useAuth();
  const [choice, setChoice] = useState(preset);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  // Hard-block: user must be logged in to purchase.
  if (!isAuthenticated) {
    return (
      <PopupShell title="Spominska svečka" onClose={onClose}>
        <p className="text-[16px] leading-[24px] text-[#1E2125]">
          Za naročilo spominske svečke se moraš prijaviti.
        </p>
        <div className="mx-auto mt-[28px] w-[250px] mobile:w-full">
          <Link
            href="/registracija"
            className="flex h-[44px] w-full items-center justify-center rounded-[4px] bg-gradient-to-b from-[#0D94E8] to-[#0A85C2] text-[16px] text-white shadow-custom-dark-bottom"
          >
            Prijava
          </Link>
        </div>
      </PopupShell>
    );
  }

  const submit = async () => {
    if (busy) return;
    if (!choice) {
      toast.error("Izberi svečko");
      return;
    }
    if (!name.trim()) {
      toast.error("Vpiši svoje ime");
      return;
    }
    setBusy(true);
    try {
      const { paymentUrl } = await orderService.createCheckout({
        product: choice,
        obituaryId: obituary.id,
        displayName: name.trim(),
      });

      if (!paymentUrl) {
        throw new Error(
          "Plačila trenutno ni mogoče pripraviti. Poskusi znova."
        );
      }

      window.location.href = paymentUrl;
    } catch (error) {
      toast.error(error?.message || "Prišlo je do napake. Poskusi znova.");
      setBusy(false);
    }
  };

  return (
    <PopupShell title="Spominska svečka" onClose={onClose}>
      {/* The phone design has no intro text; its example link sits under the candle instead. */}
      <p className="text-[16px] leading-[24px] text-[#1E2125] mobile:hidden">
        Skrbnik spominske strani jo prejme v paketu brez doplačila, drugi jo lahko naročijo.{" "}
        <ExampleLink>Tule</ExampleLink> je primer na spominski strani.
      </p>

      <div className="mt-5 flex flex-col items-center rounded-[10px] border border-[#6D778E]/70 px-4 py-6">
        <div className="rounded-[10px] bg-white p-[3px] shadow-custom-light-dark-box">
          {/* the same candle as in step 3 of the thank-you card */}
          <Image
            src="/user5.jpeg"
            alt="Animirana spominska svečka"
            width={168}
            height={220}
            className="h-[110px] w-[84px] rounded-[8px] object-cover"
          />
        </div>
        <p className="mt-3 text-center text-[16px] leading-[24px] text-[#1E2125]">
          {name.trim() || "Tvoje ime"}
        </p>
      </div>

      <p className="mt-2 hidden text-right text-[14px] leading-[20px] mobile:block">
        <ExampleLink>Primer na strani</ExampleLink>
      </p>

      <div className="mt-5 rounded-[8px] bg-[#E8F0F6] p-3 text-[13px] leading-[18px] text-[#6D778E]">
        <strong className="flex items-center gap-2 text-[14px] font-medium text-[#14105F]">
          <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
            <circle cx="8" cy="8" r="7" fill="none" stroke="#14105F" strokeWidth="1.5" />
            <text x="8" y="11.5" textAnchor="middle" fontSize="10" fontWeight="700" fill="#14105F">?</text>
          </svg>
          Dvojna korist
        </strong>
        <span className="mt-1 block">
          Spominska svečka je hkrati tudi darilo: poleg svečke in vpisa imena podarite obstoječemu skrbniku
          spominske strani hkrati tudi podaljšanje njegovega statusa skrbnika za enako število let.
        </span>
      </div>

      <div className="mt-5">
        <MemoryPageField obituary={obituary} mobileLabel={false} />
      </div>
      <div className="mt-4">
        <SelectField label="Izberi svečko" value={choice} onChange={setChoice} options={CANDLE_OPTIONS} />
      </div>
      <div className="mt-4">
        <TextFieldComp
          placeholder="Dodaj svoje ime (izpisano bo na spominski strani)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={21}
        />
      </div>

      <div className="mx-auto mt-[28px] w-[250px] mobile:w-full">
        <ButtonBlueBorder placeholder="Naroči" onClick={submit} loading={busy} />
      </div>
    </PopupShell>
  );
}
